import React from 'react';
import { Mail, ToggleLeft, ToggleRight, Trash2 } from 'lucide-react';
import { ProfileEmailAccount } from './types';

interface EmailAccountsTabProps {
    loading: boolean;
    emailAccounts: ProfileEmailAccount[];
    onAddAccount: () => void;
    onToggleStatus: (id: string) => void;
    onDeleteAccount: (id: string) => void;
}

const EmailAccountsTab: React.FC<EmailAccountsTabProps> = ({
    loading,
    emailAccounts,
    onAddAccount,
    onToggleStatus,
    onDeleteAccount
}) => {
    return (
        <div className="space-y-8">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                <div>
                    <h3 className="text-xl font-semibold text-gray-900 mb-2">Kayıtlı Email Hesapları</h3>
                    <p className="text-gray-600">
                        IMAP/POP3 email hesaplarınızı yönetin ve izleyin
                    </p>
                </div>
                <button
                    onClick={onAddAccount}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium"
                >
                    <Mail className="h-4 w-4 mr-2" />
                    Email Hesabı Ekle
                </button>
            </div>

            {loading ? (
                <div className="space-y-4">
                    {[1, 2, 3].map((i) => (
                        <div key={i} className="animate-pulse">
                            <div className="flex items-center space-x-4 p-4 border border-gray-200 rounded-lg">
                                <div className="w-12 h-12 bg-gray-200 rounded-full"></div>
                                <div className="flex-1 space-y-2">
                                    <div className="h-4 bg-gray-200 rounded w-1/3"></div>
                                    <div className="h-3 bg-gray-200 rounded w-1/4"></div>
                                </div>
                                <div className="w-20 h-8 bg-gray-200 rounded"></div>
                            </div>
                        </div>
                    ))}
                </div>
            ) : !emailAccounts || emailAccounts.length === 0 ? (
                <div className="text-center py-12">
                    <div className="mx-auto h-16 w-16 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                        <Mail className="h-8 w-8 text-gray-400" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">Henüz email hesabı eklenmemiş</h3>
                    <p className="text-gray-600 mb-6">
                        Email hesabınızı ekleyerek email taramalarını başlatabilirsiniz.
                    </p>
                    <button
                        onClick={onAddAccount}
                        className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium mx-auto"
                    >
                        <Mail className="h-4 w-4 mr-2" />
                        İlk Email Hesabınızı Ekleyin
                    </button>
                </div>
            ) : (
                <div className="space-y-4">
                    {emailAccounts.map((account) => (
                        <div key={account.id} className="border border-gray-200 rounded-xl p-6 hover:shadow-lg transition-all duration-200 hover:border-gray-300">
                            <div className="flex items-center justify-between">
                                <div className="flex items-center space-x-4">
                                    <div className="w-12 h-12 bg-blue-100 rounded-full flex items-center justify-center">
                                        <Mail className="h-6 w-6 text-blue-600" />
                                    </div>
                                    <div className="overflow-hidden">
                                        <h4 className="font-semibold text-gray-900 truncate">{account.email_address}</h4>
                                        <p className="text-sm text-gray-600 capitalize">{account.provider}</p>
                                        <p className="text-xs text-gray-500 mt-1 truncate">
                                            {account.imap_host}:{account.imap_port} {account.use_ssl ? '(SSL)' : ''}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3 ml-4">
                                    <button
                                        onClick={() => onToggleStatus(account.id)}
                                        className={`flex items-center px-3 py-2 text-sm rounded-lg font-medium transition-colors whitespace-nowrap ${account.is_active
                                                ? 'bg-green-100 text-green-800 hover:bg-green-200'
                                                : 'bg-red-100 text-red-800 hover:bg-red-200'
                                            }`}
                                    >
                                        {account.is_active ? (
                                            <ToggleRight className="h-4 w-4 mr-1" />
                                        ) : (
                                            <ToggleLeft className="h-4 w-4 mr-1" />
                                        )}
                                        {account.is_active ? 'Aktif' : 'Pasif'}
                                    </button>
                                    <button
                                        onClick={() => onDeleteAccount(account.id)}
                                        className="flex items-center px-3 py-2 text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg transition-colors"
                                    >
                                        <Trash2 className="h-4 w-4 mr-1" />
                                        <span className="hidden sm:inline">Sil</span>
                                    </button>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export default EmailAccountsTab;
